import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackPinClick } from './tracking';
// eslint-disable-next-line import/no-restricted-paths
import { trackListingShow } from '../../../../../../ListingMobile';
import { setupListingModalSingleMode } from '../../../../../../common/actions/listingModal';
import { useApplicationContext } from '../../../../../../common/containers/ApplicationContext';
import { IHotspotProperties, TFeature } from '../../../../../../common/types/map';
import { IApplicationState, TThunkDispatch } from '../../../../../../common/types/redux';
import { precisionToMinZoom, zoomToPrecision } from '../../../../../../common/utils/precision';
import { IOpenFeatureParams, openFeature as openFeatureAction } from '../../../../../actions/features';
import { selectMapBounds } from '../../../../../selectors';
import { TMapBounds } from '../../../../../types/mapBounds';
import { MAP_SET_CENTER_OPTIONS } from '../../../constants';
import { useMapContext } from '../../../context';
import { useFeaturesContext } from '../context';

interface IClickHandlersProps {
  resultsAvailable: boolean;
  detailsVisible: boolean;
}

export const ClickHandlers: React.FC<IClickHandlersProps> = ({
  resultsAvailable: isResultsAvailable,
  detailsVisible: isDetailsVisible,
}) => {
  const { config } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const { map } = useMapContext();
  const { precision } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);
  const { hotspotsLayer, balloonManager } = useFeaturesContext();

  const openFeature = React.useCallback(
    (params: Omit<IOpenFeatureParams, 'deviceType'>) => dispatch(openFeatureAction({ ...params, deviceType })),
    [dispatch, deviceType],
  );

  const handleHotspotClick = React.useCallback(
    (event: YMaps.IEvent) => {
      const currentZoom = map.getZoom();
      const [, maxPossibleZoom] = map.zoomRange.getCurrent();
      const activeObject = event.get<YMaps.IHotspotLayerObject<IHotspotProperties>>('activeObject');
      const { originalFeature } = activeObject.getProperties();

      trackPinClick({
        isDetailsVisible,
        zoom: Math.round(currentZoom),
      });

      if (!isResultsAvailable && currentZoom <= maxPossibleZoom) {
        const currentPrecision = zoomToPrecision(config, currentZoom);
        const nextPrecision = currentPrecision + 1;
        const nextPrecisionZoom = precisionToMinZoom(config, nextPrecision);
        const nextFallbackZoom = currentZoom + 2;
        const coords = originalFeature.geometry.coordinates;

        const nextZoom = Math.min(nextPrecisionZoom || nextFallbackZoom, maxPossibleZoom);
        const center: YMaps.TCoord = [coords[0], coords[1]];

        map.setCenter(center, nextZoom, MAP_SET_CENTER_OPTIONS);

        return;
      }
    },
    [map, isResultsAvailable, openFeature, precision, isDetailsVisible, config],
  );

  const handleBalloonClick = React.useCallback(
    (event: YMaps.IEvent) => {
      const objectId = event.get<string>('objectId');
      const feature = balloonManager.objectManager.objects.getById<TFeature>(objectId);
      const currentZoom = map.getZoom();

      dispatch(setupListingModalSingleMode(feature)).then(trackListingShow('pin_click'));

      trackPinClick({
        isDetailsVisible,
        zoom: Math.round(currentZoom),
      });

      openFeature({ feature, precision, isDetailed: isDetailsVisible });
    },
    [balloonManager, map, openFeature, precision, isDetailsVisible],
  );

  React.useEffect(() => {
    hotspotsLayer.layer.events.add('click', handleHotspotClick);
    balloonManager.objectManager.objects.events.add('click', handleBalloonClick);

    return () => {
      hotspotsLayer.layer.events.remove('click', handleHotspotClick);
      balloonManager.objectManager.objects.events.remove('click', handleBalloonClick);
    };
  }, [balloonManager, handleBalloonClick, handleHotspotClick, hotspotsLayer]);

  return null;
};
